import { useState } from "react";
import { Star, Plus, Trash2, X, Image, List } from "lucide-react";

/**
 * SponsorListEditor - Component for managing sponsor logos in the homepage admin
 * Supports individual and mass-add of sponsor entries with logo upload/URL
 */
const SponsorListEditor = ({ sponsors, onUpdate }) => {
  const [showMassAdd, setShowMassAdd] = useState(false);
  const [massAddUrls, setMassAddUrls] = useState("");

  const handleMassAdd = () => {
    // Split by newlines and filter empty lines
    const urls = massAddUrls
      .split('\n')
      .map(url => url.trim())
      .filter(url => url.length > 0);
    
    if (urls.length === 0) return;
    
    // Create new sponsors from URLs
    const newSponsors = urls.map(url => ({
      name: "",
      logo_url: url,
      link: ""
    }));
    
    // Add to existing sponsors
    onUpdate([...sponsors, ...newSponsors]);
    setMassAddUrls("");
    setShowMassAdd(false);
  };

  const updateSponsor = (idx, field, value) => {
    const newSponsors = [...sponsors];
    newSponsors[idx] = { ...newSponsors[idx], [field]: value };
    onUpdate(newSponsors);
  };

  const removeSponsor = (idx) => {
    const newSponsors = sponsors.filter((_, i) => i !== idx);
    onUpdate(newSponsors);
  };

  const addSponsor = () => {
    onUpdate([...sponsors, { name: "", logo_url: "", link: "" }]);
  };

  return (
    <div className="space-y-4">
      {/* Info banner for slideshow */}
      {sponsors.length > 5 && (
        <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded text-sm text-orange-400">
          <Star className="w-4 h-4 inline mr-2" />
          You have more than 5 sponsors - the section will display as an auto-scrolling slideshow!
        </div>
      )}
      
      {/* Mass Add Panel */}
      {showMassAdd && (
        <div className="p-4 bg-zinc-800 border border-orange-500/50 rounded space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-white font-medium flex items-center gap-2">
              <List className="w-4 h-4 text-orange-500" />
              Mass Add Sponsor Logos
            </h4>
            <button
              onClick={() => { setShowMassAdd(false); setMassAddUrls(""); }}
              className="text-zinc-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-zinc-400">
            Paste multiple logo URLs below (one URL per line). You can add sponsor names and website links after.
          </p>
          <textarea
            value={massAddUrls}
            onChange={(e) => setMassAddUrls(e.target.value)}
            placeholder={"https://example.com/logo1.png\nhttps://example.com/logo2.png\nhttps://example.com/logo3.png"}
            className="w-full h-32 bg-zinc-900 border border-zinc-700 text-white px-3 py-2 text-sm rounded resize-none font-mono"
            autoFocus
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-zinc-500">
              {massAddUrls.split('\n').filter(l => l.trim()).length} URL(s) detected
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => { setShowMassAdd(false); setMassAddUrls(""); }}
                className="px-3 py-1.5 text-sm text-zinc-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleMassAdd}
                disabled={!massAddUrls.trim()}
                className="px-4 py-1.5 text-sm bg-orange-500 text-black font-medium rounded hover:bg-orange-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> Add {massAddUrls.split('\n').filter(l => l.trim()).length} Logos
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Sponsor List */}
      {sponsors.map((sponsor, idx) => (
        <div key={idx} className="p-4 bg-zinc-800/50 border border-zinc-700 rounded space-y-3">
          <div className="flex items-start gap-4">
            {/* Logo Preview */}
            <div className="w-20 h-20 bg-zinc-900 border border-zinc-700 rounded flex items-center justify-center flex-shrink-0 overflow-hidden">
              {sponsor.logo_url ? (
                <img 
                  src={sponsor.logo_url} 
                  alt={sponsor.name || "Sponsor"} 
                  className="max-w-full max-h-full object-contain"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <Star className="w-8 h-8 text-zinc-600" />
              )}
            </div>
            
            {/* Sponsor Fields */}
            <div className="flex-1 space-y-2">
              <input
                type="text"
                value={sponsor.name || ""}
                onChange={(e) => updateSponsor(idx, 'name', e.target.value)}
                placeholder="Sponsor Name"
                className="w-full bg-zinc-900 border border-zinc-700 text-white px-3 py-2 text-sm rounded"
              />
              <input
                type="text"
                value={sponsor.logo_url || ""}
                onChange={(e) => updateSponsor(idx, 'logo_url', e.target.value)}
                placeholder="Logo URL (paste image URL or upload)"
                className="w-full bg-zinc-900 border border-zinc-700 text-white px-3 py-2 text-sm rounded"
              />
              <input
                type="text"
                value={sponsor.link || ""}
                onChange={(e) => updateSponsor(idx, 'link', e.target.value)}
                placeholder="Website URL (https://sponsor-website.com)"
                className="w-full bg-zinc-900 border border-zinc-700 text-white px-3 py-2 text-sm rounded"
              />
              
              {/* File Upload for Logo */}
              <div className="flex items-center gap-2">
                <label className="cursor-pointer text-xs text-orange-500 hover:text-orange-400 flex items-center gap-1">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          updateSponsor(idx, 'logo_url', event.target.result);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                  <Image className="w-3 h-3" /> Upload Logo
                </label>
                <span className="text-xs text-zinc-500">or paste URL above</span>
              </div>
            </div>
            
            {/* Delete Button */}
            <button
              onClick={() => removeSponsor(idx)}
              className="p-2 text-red-500 hover:bg-red-500/10 rounded"
              title="Remove sponsor"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
      
      {/* Add Sponsor Buttons */}
      <div className="flex gap-3">
        <button
          onClick={addSponsor}
          className="flex-1 py-3 border-2 border-dashed border-zinc-700 hover:border-orange-500 text-zinc-400 hover:text-orange-500 rounded transition-colors flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" /> Add Sponsor
        </button>
        <button
          onClick={() => setShowMassAdd(true)}
          className="px-4 py-3 bg-zinc-800 border border-zinc-700 hover:border-orange-500 text-zinc-400 hover:text-orange-500 rounded transition-colors flex items-center justify-center gap-2"
          title="Add multiple sponsor logos at once"
        >
          <List className="w-4 h-4" /> Mass Add
        </button>
      </div>
      
      {/* Tips */}
      <div className="text-xs text-zinc-500 space-y-1">
        <p>• Add sponsor logos with clickable links to their websites</p>
        <p>• Upload images or paste URLs from external image hosts</p>
        <p>• Use <strong>Mass Add</strong> to paste multiple logo URLs at once</p>
        <p>• With more than 5 sponsors, logos will auto-scroll as a slideshow</p>
      </div>
    </div>
  );
};

export default SponsorListEditor;
